import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import './CisBenchmarks.css';

const CisBenchmarks = () => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('cisControls'); // 'cisControls' | 'generalControls' | 'controlSettings'

  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState(null);

  const [controls, setControls] = useState([]);
  const [controlsLoading, setControlsLoading] = useState(true);
  const [controlsError, setControlsError] = useState(null);
  const [updatingControlId, setUpdatingControlId] = useState(null);

  const fetchAudit = useCallback(async () => {
    if (!selectedTenant) {
      setAuditError('No tenant selected');
      setAuditLoading(false);
      return;
    }
    try {
      setAuditLoading(true);
      setAuditError(null);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/cis/audit?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        setAuditError(data.error || 'Failed to fetch CIS audit results');
        setAuditRows([]);
      } else {
        setAuditRows(Array.isArray(data.audits) ? data.audits : []);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error fetching CIS audit results:', e);
      setAuditError('Error fetching CIS audit results');
      setAuditRows([]);
    } finally {
      setAuditLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  const fetchControls = useCallback(async () => {
    if (!selectedTenant) {
      setControlsError('No tenant selected');
      setControlsLoading(false);
      return;
    }
    try {
      setControlsLoading(true);
      setControlsError(null);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/cis/controls?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        setControlsError(data.error || 'Failed to fetch CIS controls');
        setControls([]);
      } else {
        setControls(Array.isArray(data.controls) ? data.controls : []);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error fetching CIS controls:', e);
      setControlsError('Error fetching CIS controls');
      setControls([]);
    } finally {
      setControlsLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    fetchAudit();
    fetchControls();
  }, [fetchAudit, fetchControls]);

  const toggleControl = async (control) => {
    if (!selectedTenant || !control || control.id == null) {
      return;
    }
    const nextToggled = !isControlEnabled(control);
    try {
      setUpdatingControlId(control.id);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/cis/controls/toggle?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: control.id, toggle: nextToggled })
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        // eslint-disable-next-line no-console
        console.error('Failed to toggle control:', data.error);
        return;
      }
      setControls(prev =>
        prev.map(c =>
          c.id === control.id
            ? { ...c, toggle: nextToggled ? 1 : 0 }
            : c
        )
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error toggling CIS control:', e);
    } finally {
      setUpdatingControlId(null);
    }
  };

  const isControlEnabled = (control) => {
    const value = control?.toggle;
    // Handle tinyint(1) - can be 0/1, true/false, or string '0'/'1'
    if (typeof value === 'string') {
      return value === '1' || value.toLowerCase() === 'true';
    }
    return Boolean(value);
  };

  const computeControlPass = (control) => {
    const rawScore = Number(control.scoreInPercentage) || 0;
    const hasUserDefined = control.UserDefinedPassingScoreInPercentage !== null &&
      control.UserDefinedPassingScoreInPercentage !== undefined &&
      control.UserDefinedPassingScoreInPercentage !== '';
    const threshold = hasUserDefined
      ? Number(control.UserDefinedPassingScoreInPercentage) || 0
      : 100;
    const pass = rawScore >= threshold;
    return {
      pass,
      score: rawScore,
      threshold
    };
  };

  const renderStatusBadge = (status) => {
    const normalized = (status || '').toString().toLowerCase();
    const isPass = normalized === 'success' || normalized === 'pass' || normalized === 'ok';
    const className = isPass ? 'cis-status-badge pass' : 'cis-status-badge fail';
    const label = isPass ? 'PASS' : 'FAIL';
    return <span className={className}>{label}</span>;
  };

  const renderCisControlsTab = () => {
    if (auditLoading) {
      return <div className="cis-loading">Loading CIS audit results...</div>;
    }
    if (auditError) {
      return <div className="cis-error">{auditError}</div>;
    }
    if (!auditRows.length) {
      return <div className="cis-empty">No CIS audit results available for this tenant.</div>;
    }

    return (
      <div className="cis-table-wrapper">
        <table className="cis-table">
          <thead>
            <tr>
              <th>Control</th>
              <th>Description</th>
              <th>Last Audit</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {auditRows.map((row, idx) => (
              <tr key={`${row.Control || row.control || idx}`}>
                <td>{row.Control || row.control}</td>
                <td>{row.Description || row.description}</td>
                <td>
                  {row.CheckDate || row.checkdate
                    ? new Date(row.CheckDate || row.checkdate).toLocaleDateString()
                    : '—'}
                </td>
                <td>{renderStatusBadge(row.Status || row.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderGeneralControlsTab = () => {
    if (controlsLoading) {
      return <div className="cis-loading">Loading CIS controls...</div>;
    }
    if (controlsError) {
      return <div className="cis-error">{controlsError}</div>;
    }
    const enabledControls = controls.filter(c => isControlEnabled(c));
    if (!enabledControls.length) {
      return <div className="cis-empty">No enabled CIS controls for this tenant.</div>;
    }

    return (
      <div className="cis-table-wrapper">
        <table className="cis-table">
          <thead>
            <tr>
              <th>Control</th>
              <th>Description</th>
              <th>Score (%)</th>
              <th>Passing Threshold (%)</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {enabledControls.map(control => {
              const { pass, score, threshold } = computeControlPass(control);
              return (
                <tr key={control.id}>
                  <td>{control.controlname}</td>
                  <td>{control.description}</td>
                  <td>{Number.isNaN(score) ? '—' : score.toFixed(1)}</td>
                  <td>{Number.isNaN(threshold) ? '—' : threshold.toFixed(1)}</td>
                  <td>
                    <span className={`cis-status-badge ${pass ? 'pass' : 'fail'}`}>
                      {pass ? 'PASS' : 'FAIL'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderControlSettingsTab = () => {
    if (controlsLoading) {
      return <div className="cis-loading">Loading CIS controls...</div>;
    }
    if (controlsError) {
      return <div className="cis-error">{controlsError}</div>;
    }
    if (!controls.length) {
      return <div className="cis-empty">No CIS controls defined for this tenant.</div>;
    }

    return (
      <div className="cis-settings-list">
        {controls.map(control => {
          const enabled = isControlEnabled(control);
          const { pass, score, threshold } = computeControlPass(control);
          return (
            <div key={control.id} className="cis-settings-item">
              <div className="cis-settings-main">
                <div className="cis-settings-header">
                  <h3>{control.controlname}</h3>
                  <button
                    type="button"
                    className={`cis-toggle ${enabled ? 'on' : 'off'}`}
                    onClick={() => toggleControl(control)}
                    disabled={updatingControlId === control.id}
                  >
                    <span className="cis-toggle-thumb" />
                    <span className="cis-toggle-label">
                      {enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </button>
                </div>
                <p className="cis-settings-description">
                  {control.description}
                </p>
              </div>
              <div className="cis-settings-meta">
                <div className="cis-meta-row">
                  <span className="cis-meta-label">Score</span>
                  <span className="cis-meta-value">
                    {Number.isNaN(score) ? '—' : `${score.toFixed(1)}%`}
                  </span>
                </div>
                <div className="cis-meta-row">
                  <span className="cis-meta-label">Passing Threshold</span>
                  <span className="cis-meta-value">
                    {Number.isNaN(threshold) ? '—' : `${threshold.toFixed(1)}%`}
                  </span>
                </div>
                <div className="cis-meta-row">
                  <span className="cis-meta-label">Result</span>
                  <span className="cis-meta-value">
                    <span className={`cis-status-badge ${pass ? 'pass' : 'fail'}`}>
                      {pass ? 'PASS' : 'FAIL'}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="cis-container">
      <div className="cis-header">
        <h2>CIS Benchmarks</h2>
        <p>Monitor CIS controls, evaluate compliance, and configure control settings.</p>
      </div>

      <div className="cis-tabs">
        <button
          type="button"
          className={`cis-tab ${activeTab === 'cisControls' ? 'active' : ''}`}
          onClick={() => setActiveTab('cisControls')}
        >
          CIS Controls
        </button>
        <button
          type="button"
          className={`cis-tab ${activeTab === 'generalControls' ? 'active' : ''}`}
          onClick={() => setActiveTab('generalControls')}
        >
          General Controls
        </button>
        <button
          type="button"
          className={`cis-tab ${activeTab === 'controlSettings' ? 'active' : ''}`}
          onClick={() => setActiveTab('controlSettings')}
        >
          Control Settings
        </button>
      </div>

      <div className="cis-tab-content">
        {activeTab === 'cisControls' && renderCisControlsTab()}
        {activeTab === 'generalControls' && renderGeneralControlsTab()}
        {activeTab === 'controlSettings' && renderControlSettingsTab()}
      </div>
    </div>
  );
};

export default CisBenchmarks;

